import React, { useMemo, useState } from 'react';
import {
  type DocumentElement as DocumentElementType,
  COUNTRIES,
  flagEmoji,
  matchCountry,
  countryByCode,
} from '@eaco/forms-core';
import { FieldHeader, FieldErrors, FieldHelpText } from './FieldParts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../lib/utils';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

const Chevron = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="flex-none text-[var(--forms-faint)]"
    aria-hidden
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * Country (Elements Library): a searchable flag + name picker. Stores the
 * ISO alpha-2 code; the trigger shows the selected flag + name (filled
 * tint) or a placeholder. The list filters by name or code.
 */
const CountryInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [touched, setTouched] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    placeholder?: string;
    defaultValue?: string;
  };

  const label = data?.label || 'Country';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;
  const placeholder = data?.placeholder || 'Select a country…';

  const defaultForHydration = data?.defaultValue
    ? String(data.defaultValue).toUpperCase()
    : undefined;
  const displayValue = useDefaultValue(value, setValue, defaultForHydration);
  const selected = countryByCode(displayValue);

  const filtered = useMemo(
    () => COUNTRIES.filter((c) => matchCountry(c, search)),
    [search],
  );

  const errors =
    isEditor || !touched || !required || selected
      ? []
      : ['This field is required'];

  // Read-only: static flag + name.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="flex items-center gap-[8px] text-sm">
          {selected ? (
            <>
              <span className="text-[16px] leading-none">
                {flagEmoji(selected.code)}
              </span>
              <span>{selected.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={element.id}
            type="button"
            className={cn(
              'flex w-full items-center gap-[10px] rounded-[var(--forms-input-radius)] border-[length:var(--forms-input-border-w)] px-[13px] py-[10px] text-left transition-colors',
              selected
                ? 'border-[var(--forms-input-filled-border)] bg-[var(--forms-input-filled-bg)]'
                : 'border-[var(--forms-border)] bg-white',
              open &&
                'border-[var(--forms-accent,var(--forms-accent-default))] shadow-[0_0_0_3px_var(--forms-accent-ring,var(--forms-accent-ring-default))]',
            )}
          >
            {selected ? (
              <>
                <span className="text-[18px] leading-none">
                  {flagEmoji(selected.code)}
                </span>
                <span className="flex-1 truncate text-[length:var(--forms-input-font)] font-semibold text-[var(--forms-ink)]">
                  {selected.name}
                </span>
              </>
            ) : (
              <span className="flex-1 truncate text-[length:var(--forms-input-font)] text-[var(--forms-faint)]">
                {placeholder}
              </span>
            )}
            <Chevron />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0">
          <div className="border-b p-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries…"
              className="w-full rounded-[8px] border border-[var(--forms-border)] bg-white px-[10px] py-[7px] text-[13px] text-[var(--forms-ink)] placeholder:text-[var(--forms-faint)] focus:border-[var(--forms-accent,var(--forms-accent-default))] focus:outline-none"
            />
          </div>
          <ul className="max-h-[240px] overflow-y-auto py-1">
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => {
                    setTouched(true);
                    setValue(c.code);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    'flex w-full items-center gap-[10px] px-[12px] py-[8px] text-left text-[13px] hover:bg-[var(--forms-addon-bg)]',
                    selected?.code === c.code &&
                      'bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))]',
                  )}
                >
                  <span className="text-[15px] leading-none">
                    {flagEmoji(c.code)}
                  </span>
                  <span className="flex-1 truncate text-[var(--forms-ink)]">
                    {c.name}
                  </span>
                  <span className="text-[11px] text-[var(--forms-muted)]">
                    {c.code}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-[12px] py-[10px] text-[13px] text-[var(--forms-muted-soft)]">
                No matches
              </li>
            )}
          </ul>
        </PopoverContent>
      </Popover>
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default CountryInput;
